<script setup lang="ts">
import { computed, watch } from 'vue';
import RadioButton from 'primevue/radiobutton';
import { ChargeEligibilitySchema, PlacementCollectSchema } from '@neon-one/auctions-types';
import type { PlacementCollect, ReassignTarget } from '@neon-one/auctions-types';

import { firstNameOf } from '../../reassign-target';

const CollectChoice = PlacementCollectSchema.enum;
const ChargeUnavailableReason = ChargeEligibilitySchema.options[1].shape.reason.enum;

type CollectTarget = Pick<ReassignTarget, 'bidderUuid' | 'name' | 'cardLabel' | 'chargeEligibility'>;

const props = defineProps<{
    /** Undefined means "take payment later": no charge and no checkout email. */
    collect: PlacementCollect | undefined;
    selected: CollectTarget | null;
    canCharge: boolean;
    /** An archived auction has no checkout site to email a link to. */
    archived?: boolean;
}>();
const emit = defineEmits<{ 'update:collect': [PlacementCollect | undefined] }>();
// The radio's value for "take payment later", which the model carries as undefined.
const PAY_LATER = 'later';

const firstName = computed(() => firstNameOf(props.selected?.name));
const chargeAvailable = computed(() => props.canCharge
    && !!props.selected?.cardLabel && props.selected.chargeEligibility?.eligible === true);
const fallbackCollect = computed(() => props.archived ? undefined : CollectChoice.notify);
const effectiveCollect = computed(() => chargeAvailable.value ? props.collect : fallbackCollect.value);
const chargeUnavailableReason = computed(() => {
    const eligibility = props.selected?.chargeEligibility;
    if (eligibility?.eligible === false && eligibility.reason === ChargeUnavailableReason.not_auto_charge) {
        return 'This auction does not charge cards automatically.';
    }
    return `${firstName.value} has no card on file.`;
});

watch([() => props.selected?.bidderUuid, chargeAvailable], ([bidderUuid, available], previous) => {
    const defaultCollect = available ? CollectChoice.charge : fallbackCollect.value;
    const targetChanged = previous.length > 0 && bidderUuid !== previous[0];
    const availabilityChanged = previous.length > 0 && available !== previous[1];
    if ((targetChanged || availabilityChanged || !available) && props.collect !== defaultCollect) {
        emit('update:collect', defaultCollect);
    }
}, { immediate: true });
</script>

<template>
    <fieldset class="ri__group">
        <legend class="ri__legend">
            Payment
        </legend>
        <div
            v-if="chargeAvailable"
            class="app-checkbox-group ri__mode"
            :class="{ 'ri__mode--selected': effectiveCollect === CollectChoice.charge }"
            data-testid="ri__collect-charge"
        >
            <RadioButton
                :model-value="effectiveCollect"
                :value="CollectChoice.charge"
                input-id="ri-collect-charge"
                name="ri-collect"
                @update:model-value="emit('update:collect', CollectChoice.charge)"
            />
            <label
                class="app-field-label ri__mode-text"
                for="ri-collect-charge"
            >
                <span class="ri__mode-title">Charge {{ selected?.cardLabel }} now</span>
                <span class="ri__mode-desc text-caption">
                    The card on file is charged when you confirm. {{ firstName }} gets a receipt by email.
                </span>
            </label>
        </div>
        <div
            v-if="archived"
            class="app-checkbox-group ri__mode"
            :class="{ 'ri__mode--selected': effectiveCollect === undefined }"
            data-testid="ri__collect-later"
        >
            <RadioButton
                :model-value="effectiveCollect ?? PAY_LATER"
                :value="PAY_LATER"
                input-id="ri-collect-later"
                name="ri-collect"
                @update:model-value="emit('update:collect', undefined)"
            />
            <label
                class="app-field-label ri__mode-text"
                for="ri-collect-later"
            >
                <span class="ri__mode-title">Take payment later</span>
                <span class="ri__mode-desc text-caption">
                    Adds it to an unpaid invoice. Take their payment in the Console.
                </span>
            </label>
        </div>
        <div
            v-else
            class="app-checkbox-group ri__mode"
            :class="{ 'ri__mode--selected': effectiveCollect === CollectChoice.notify }"
            data-testid="ri__collect-notify"
        >
            <RadioButton
                :model-value="effectiveCollect"
                :value="CollectChoice.notify"
                input-id="ri-collect-notify"
                name="ri-collect"
                @update:model-value="emit('update:collect', CollectChoice.notify)"
            />
            <label
                class="app-field-label ri__mode-text"
                for="ri-collect-notify"
            >
                <span class="ri__mode-title">Notify {{ firstName }} to pay</span>
                <span class="ri__mode-desc text-caption">
                    Emails their invoice with a checkout link. Nothing is charged until they pay.
                </span>
            </label>
        </div>
        <p
            v-if="!chargeAvailable"
            class="ri__muted text-caption"
            data-testid="ri__collect-unavailable"
        >
            Charging a card is not available: {{ chargeUnavailableReason }}
        </p>
    </fieldset>
</template>

<style scoped>
.ri__group {
    margin: var(--app-space-0);
    padding: var(--app-space-0);
    border: none;
    display: flex;
    flex-direction: column;
    gap: var(--app-space-8);
}
.ri__legend {
    padding: var(--app-space-0);
    margin-bottom: var(--app-space-8);
    font-size: var(--app-font-size-body-sm);
    font-weight: var(--app-font-weight-semibold);
    color: var(--app-text-primary);
}
.ri__mode {
    display: flex;
    align-items: flex-start;
    gap: var(--app-space-12);
    padding: var(--app-space-12);
    border: var(--app-border-width-default) solid var(--app-border-muted);
    border-radius: var(--app-radius-md);
    cursor: pointer;
}
.ri__mode:hover {
    border-color: var(--app-border-hover);
}
.ri__mode--selected {
    border-color: var(--app-border-active);
    background: var(--app-highlight-background);
}
.ri__mode-text {
    display: flex;
    flex: 1;
    cursor: pointer;
    flex-direction: column;
    gap: var(--app-space-4);
    min-width: 0;
}
.ri__mode-title {
    font-weight: var(--app-font-weight-semibold);
}
.ri__mode-desc {
    color: var(--app-text-primary);
}
.ri__muted {
    margin: var(--app-space-0);
    color: var(--app-text-muted);
}
</style>
